import { useMutation } from '@tanstack/react-query';
import { loginUser } from 'api/userApi';
import { queryClient } from 'App';
import LoadingButton from 'components/fantaclasse/LoadingButton';
import { useAuth } from 'context/AuthProvider';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, FloatingLabel, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { supabase } from 'supabase/client';
import AccountNotice from './AccountNotice';
import TurnstileCaptcha from './captcha/TurnstileCaptcha';
import SocialAuthButtons from './SocialAuthButtons';
import Divider from 'components/common/Divider';

const LoginForm = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (user) {
      navigate('/classe');
    }
  }, [user, navigate]);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');

  // Validators
  const isValidEmail = email => /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
  const isValidUsername = username => /^[a-zA-Z0-9_]{4,16}$/.test(username);

  // Login functions
  const loginWithEmail = async () => {
    try {
      const {
        data: { user, session },
        error
      } = await login(username, password, token);
      if (error) throw new Error(error.message);
      finalizeLogin(user, session);
    } catch (error) {
      toast.error(error.message);
      // Reset Turnstile
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    }
  };

  const loginWithUsername = async () => {
    return mutation.mutateAsync({
      username,
      password,
      token
    });
  };

  const mutation = useMutation({
    mutationFn: loginUser,
    onSuccess: async ({ data: { access_token, refresh_token } }) => {
      const {
        data: { user, session },
        error: sessionError
      } = await supabase.auth.setSession({
        access_token,
        refresh_token
      });
      if (sessionError) toast.error(sessionError.message); //TODO: Custom error
      else finalizeLogin(user, session);
    },
    onError: () => {
      // Reset Turnstile
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    }
  });

  const handleSubmit = async e => {
    e.preventDefault();
    if (!password || !username || !token) {
      toast.error('Completa tutti i campi e verifica il captcha');
      return;
    }
    setLoading(true);
    try {
      if (isValidEmail(username)) {
        await loginWithEmail();
      } else if (isValidUsername(username)) {
        await loginWithUsername();
      } else {
        toast.error('Username non valido.');
      }
    } catch (error) {
      toast.error('Username o password errati');
    } finally {
      setLoading(false);
    }
  };

  const finalizeLogin = (user, session) => {
    if (user && session) {
      queryClient.invalidateQueries({ queryKey: ['userData'] });
      window.location.href = '/classe';
    }
  };

  const handleTurnstileSuccess = useCallback(token => {
    setToken(token);
  }, []);

  return (
    <Form onSubmit={handleSubmit}>
      <SocialAuthButtons authActionText="Accedi" />
      <Divider className="mt-4">oppure</Divider>
      <Form.Group className="mb-3">
        <FloatingLabel controlId="floatingUsername" label="Username o Email">
          <Form.Control
            placeholder={'Username o Email'}
            value={username}
            name="username"
            onChange={e => setUsername(e.target.value)}
          />
        </FloatingLabel>
      </Form.Group>

      <Form.Group className="mb-3">
        <FloatingLabel
          controlId="floatingPassword"
          label="Password"
          className="mb-3"
        >
          <Form.Control
            placeholder={'Password'}
            value={password}
            name="password"
            onChange={e => setPassword(e.target.value)}
            type="password"
          />
        </FloatingLabel>
      </Form.Group>

      <Row className="justify-content-between align-items-center">
        <Col xs="auto">
          <Form.Check type="checkbox" id="rememberMe" className="mb-0">
            <Form.Check.Input
              type="checkbox"
              name="remember"
              checked={remember}
              onChange={e => setRemember(e.target.checked)}
            />
            <Form.Check.Label className="mb-0 text-700">
              Ricordami
            </Form.Check.Label>
          </Form.Check>
        </Col>

        <Col xs="auto">
          <Link className="fs-10 mb-0" to={`/forgot-password`}>
            Password dimenticata?
          </Link>
        </Col>
      </Row>

      <TurnstileCaptcha onSuccess={handleTurnstileSuccess} ref={turnstileRef} />

      <LoadingButton
        text="Log in"
        isLoading={loading}
        disabled={!username || !password || !token}
        className="w-100"
        type="submit"
      />
      <AccountNotice />
    </Form>
  );
};

LoginForm.propTypes = {
  hasLabel: PropTypes.bool
};

export default LoginForm;
